import type { SchemaMappings as SchemaMappingsType } from '../../lib/api';
import type {
  ArrayItemType,
  DocLink,
  ExposureSurface,
  JsonSchemaFormat,
  JsonSchemaType,
  MappingOption,
  SchemaProperty,
} from './schema-editor.types';

/**
 * Pure constants and helpers for the Data Integration schema editor. Types live
 * in {@link ./schema-editor.types}.
 *
 * Everything here is stateless and side-effect free so it can be shared by the
 * orchestrator ({@link ../SchemaDefinitionsWithMappings}) and its presentational
 * sibling components without pulling React state into scope. The logic is ported
 * verbatim from the previous single-file implementation — no behavioral change.
 */

export const TYPE_LABELS: Record<JsonSchemaType, string> = {
  string: 'String',
  number: 'Number',
  integer: 'Integer',
  boolean: 'Boolean',
  array: 'Array',
  object: 'Object',
  reference: 'Reference',
};

export const FORMAT_LABELS: Record<JsonSchemaFormat, string> = {
  'date-time': 'date-time',
  date: 'date',
  time: 'time',
  email: 'email',
  uri: 'uri',
  'uri-reference': 'uri-reference',
  uuid: 'uuid',
  hostname: 'hostname',
  ipv4: 'ipv4',
  ipv6: 'ipv6',
};

export const JSON_SCHEMA_TYPES = Object.keys(TYPE_LABELS) as JsonSchemaType[];
export const ARRAY_ITEM_TYPES = JSON_SCHEMA_TYPES.filter(type => type !== 'array') as ArrayItemType[];
export const JSON_SCHEMA_FORMATS = Object.keys(FORMAT_LABELS) as JsonSchemaFormat[];
export const SCHEMA_MAPPING_METADATA_KEYS = ['urlPattern'] as const;

export const REFERENCE_PREFIX = 'reach:schemas/';

export const EXPOSURE_SURFACES: ExposureSurface[] = [
  {
    key: 'segments',
    title: 'Segments',
    help: 'Usable as a filter when you or a tenant builds a marketing segment.',
  },
  {
    key: 'mergeFields',
    title: 'Merge fields',
    help: 'Available as a dynamic variable inside email and SMS bodies.',
  },
  {
    key: 'agentTools',
    title: 'Agentic tools',
    help: 'Returned to the AI voice assistant when it looks up a record.',
  },
];

const DOCS_BASE = 'https://docs.embedreach.com';

/** Setup-guide links shown in the header "Setup guides" menu and the empty state. */
export const DOC_LINKS: DocLink[] = [
  {
    title: 'Plan your automations',
    description: "Start from a tenant's goal and work backward to the data you need",
    href: `${DOCS_BASE}/onboarding/planning/engage-automations`,
  },
  {
    title: 'How Reach models your data',
    description: 'The three core concepts and how your data maps to them',
    href: `${DOCS_BASE}/data-sharing/data-model`,
  },
  {
    title: 'Custom schemas',
    description: 'JSON Schema format, references between schemas, and PII annotations',
    href: `${DOCS_BASE}/data-sharing/custom-schemas`,
  },
  {
    title: 'Schema API reference',
    description: 'Create and update schema definitions via the API',
    href: `${DOCS_BASE}/api-reference/endpoint/post-partner-schema-definitions`,
  },
  {
    title: 'Data sync setup',
    description: 'Step-by-step onboarding for your integration',
    href: `${DOCS_BASE}/onboarding/data-sync`,
  },
];

/** Human label for a schema category. */
export function getCategoryLabel(category: string): string {
  const labels: Record<string, string> = {
    contacts_schema: 'Contacts',
    transactions_schema: 'Transactions',
    locations_schema: 'Locations',
    custom_schema: 'Custom',
    combined_schema: 'Combined',
    customer_fields: 'Customer Fields',
  };
  return labels[category] ?? category;
}

/** Friendly text for a transaction-filter operator. */
export function getOperatorDisplayText(operator: string): string {
  const operatorMap: Record<string, string> = {
    '==': 'equals',
    '!=': 'not equals',
    '>': 'greater than',
    '<': 'less than',
    '>=': 'greater or equal',
    '<=': 'less or equal',
    in: 'in',
    '!in': 'not in',
    array_contains: 'array contains',
  };
  return operatorMap[operator] ?? operator;
}

/** Whether a mapping type is required for the given schema category. */
export function isRequiredMapping(mappingType: string, schemaCategory: string): boolean {
  const requiredMappings: Record<string, string[]> = {
    contacts_schema: ['userId', 'createdDate'],
    locations_schema: ['id_field'],
    transactions_schema: ['id_field', 'userId'],
  };
  return requiredMappings[schemaCategory]?.includes(mappingType) ?? false;
}

/** The core-concept mapping targets available for a schema category. */
export function getMappingOptions(schemaCategory: string): MappingOption[] {
  const baseOptions: MappingOption[] = [{ value: 'none', label: 'No Mapping' }];

  switch (schemaCategory) {
    case 'contacts_schema':
      return [
        ...baseOptions,
        { value: 'email', label: 'Email' },
        { value: 'phone', label: 'Phone' },
        { value: 'userId', label: 'User ID', required: true },
        { value: 'firstName', label: 'First Name' },
        { value: 'lastName', label: 'Last Name' },
        { value: 'fullName', label: 'Full Name' },
        { value: 'location', label: 'Location ID' },
        { value: 'pointOfContactName', label: 'Point-of-contact name' },
        { value: 'pointOfContactEmail', label: 'Point-of-contact email' },
        { value: 'status', label: 'Status' },
        { value: 'createdDate', label: 'Created Date', required: true },
        { value: 'emailOptOut', label: 'Email Opt Out' },
        { value: 'emailOptIn', label: 'Email Opt In' },
        { value: 'emailOptOutTimestamp', label: 'Email Opt Out Timestamp' },
        { value: 'smsOptOut', label: 'SMS Opt Out' },
        { value: 'smsOptIn', label: 'SMS Opt In' },
        { value: 'smsOptOutTimestamp', label: 'SMS Opt Out Timestamp' },
        { value: 'hipaaConsent', label: 'HIPAA Consent' },
      ];

    case 'locations_schema':
      return [
        ...baseOptions,
        { value: 'id_field', label: 'ID Field', required: true },
        { value: 'name', label: 'Name' },
        { value: 'address_line_1', label: 'Address Line 1' },
        { value: 'address_line_2', label: 'Address Line 2' },
        { value: 'address_line_3', label: 'Address Line 3' },
        { value: 'locality', label: 'City' },
        { value: 'district', label: 'State/Province' },
        { value: 'postal_code', label: 'Postal Code' },
        { value: 'country_code', label: 'Country Code' },
        { value: 'phone', label: 'Phone' },
        { value: 'email', label: 'Email' },
        { value: 'website', label: 'Website' },
      ];

    case 'transactions_schema':
      return [
        ...baseOptions,
        { value: 'id_field', label: 'ID Field', required: true },
        { value: 'userId', label: 'User ID', required: true },
        { value: 'amount', label: 'Amount' },
        { value: 'dates.createdDate', label: 'Created Date' },
        { value: 'dates.paidDate', label: 'Paid Date' },
        { value: 'dates.dueDate', label: 'Due Date' },
        { value: 'dates.startDate', label: 'Start Date' },
        { value: 'dates.endDate', label: 'End Date' },
        { value: 'locationId', label: 'Location ID' },
      ];

    default:
      return baseOptions;
  }
}

/** Extracts the editable property list from a JSON Schema object. */
export function extractSchemaProperties(schema: Record<string, unknown>): SchemaProperty[] {
  const properties = (schema.properties as Record<string, unknown>) || {};
  const required = (schema.required as string[]) || [];

  if (!properties || Object.keys(properties).length === 0) {
    return [];
  }

  return Object.entries(properties).map(([name, prop]) => {
    const propObj = prop as Record<string, unknown>;
    const ref = propObj.$ref as string;

    if (ref && ref.startsWith(REFERENCE_PREFIX)) {
      return {
        name,
        type: 'reference',
        $ref: ref,
        description: (propObj.description as string) || '',
        required: required.includes(name),
        enum: undefined,
        format: undefined,
      };
    }

    const baseProperty: SchemaProperty = {
      name,
      type: (propObj.type || 'string') as JsonSchemaType,
      description: (propObj.description as string) || '',
      format: propObj.format ? (propObj.format as JsonSchemaFormat) : undefined,
      required: required.includes(name),
      enum: propObj.enum ? (propObj.enum as string[]) : undefined,
      $ref: undefined,
    };

    if (propObj.type === 'array' && propObj.items) {
      const items = propObj.items as Record<string, unknown>;
      if (items.$ref && typeof items.$ref === 'string' && items.$ref.startsWith(REFERENCE_PREFIX)) {
        return { ...baseProperty, itemsType: 'reference', itemsRef: items.$ref as string };
      }
      if (items.type) {
        return {
          ...baseProperty,
          itemsType: items.type as ArrayItemType,
          itemsFormat: items.format ? (items.format as JsonSchemaFormat) : undefined,
        };
      }
    }

    return baseProperty;
  });
}

/** Resolves the display name (schema.name) for a `reach:schemas/<id>` ref. */
export function resolveReferenceLabel(
  ref: string | undefined,
  schemas: Array<{ id: string; name: string }>,
): { id: string; name: string } | undefined {
  if (!ref) return undefined;
  const refId = ref.substring(REFERENCE_PREFIX.length);
  const match = schemas.find(s => s.id === refId || s.name === refId);
  return match ? { id: match.id, name: match.name } : undefined;
}

/** Returns the URL pattern currently stored for a schema, if any. */
export function readUrlPattern(
  category: string,
  schemaId: string,
  contactSourceType: 'primary' | 'additional',
  additionalSourceIndex: number,
  mappings: SchemaMappingsType | undefined,
): string {
  if (category === 'contacts_schema') {
    const source =
      contactSourceType === 'primary'
        ? mappings?.contactsSchema?.primarySource
        : mappings?.contactsSchema?.additionalSources?.[additionalSourceIndex];
    return source?.urlPattern ?? '';
  }
  if (category === 'transactions_schema') {
    const txn = mappings?.transactionsSchema?.find(t => t.schemaId === schemaId);
    return txn?.urlPattern ?? '';
  }
  return '';
}
